import {
  BASELINE_TYPE,
  DATA_TYPE,
  DISPLAY_UNIT,
  SFOC_OR_LOAD,
  UTC_TIMESTAMP,
  VIEW_BY_TYPE
} from '@constants/index';
import { getValue, roundToSixDecimals } from '@helpers';
import { findSignal, getGeneratorSignalsMap, findDrillingEvent, findEquipmentSignal } from '@api/helpers';
import { getDisplayUnit } from '@store/features';
import { getBaselineComparisonSlice } from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';
import {
  getMainDurationDays,
  getMainDurationHours,
  getMainPageSlice
} from '@store/features/filters/MainPageSlice/MainPageSlice';

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDisplayUnit } from './DisplayUnit';
import moment from 'moment/moment';

const nullOutIfEmpty = (main) => {
  if (!Object.values(main).some(v => v !== 0)) {
    Object.keys(main).forEach(key => { main[key] = null; });
  }
};

export const useBaselineComp = () => {
  const displayUnit = useSelector(getDisplayUnit);
  const mainDurationHours = useSelector(getMainDurationHours);
  const mainDurationDays = useSelector(getMainDurationDays);
  const { getUnitTitle, convertUnitValue } = useDisplayUnit();
  const { startDate, endDate } = useSelector(getMainPageSlice);
  const {
    selectedBaseline,
    equipment,
    analytics,
    powerMgmt,
    periodicOverview,
    drillingActivities
  } = useSelector(getBaselineComparisonSlice);

  const getTargetBaselineValue = useCallback(
    (viewBy, unit = null) => {
      if (!selectedBaseline) return null;
      let value = 0;
      let valueUnit = unit ?? displayUnit;
      switch (valueUnit) {
        case DISPLAY_UNIT.ENERGY:
          value = selectedBaseline.energyConsumption;
          break;
        case DISPLAY_UNIT.FUEL:
          value = selectedBaseline.fuelConsumption;
          break;
        case DISPLAY_UNIT.CO2:
          value = selectedBaseline.co2Emissions;
          break;
        case DISPLAY_UNIT.NOX:
          value = selectedBaseline.noxEmissions;
          break;
      }
      if (selectedBaseline.daily) {
        if (viewBy === VIEW_BY_TYPE.HOURLY) {
          return value / 24;
        }
        return value;
      }

      if (viewBy === VIEW_BY_TYPE.DAILY) {
        return value / mainDurationDays;
      }
      return value / mainDurationHours;
    },
    [displayUnit, selectedBaseline, mainDurationHours]
  );

  const { start, end } = useMemo(() => {
    const { startDate: bStart, endDate: bEnd, baselineTypeId } = selectedBaseline;

    let start = moment(startDate);
    let baseDuration = moment
      .duration(moment.parseZone(bEnd).startOf('day').diff(moment.parseZone(bStart)))
      .asDays();
    let mainDuration = moment
      .duration(moment.parseZone(endDate).diff(moment.parseZone(startDate)))
      .asDays();
    let duration = baseDuration > mainDuration ? baseDuration : mainDuration;
    if (baselineTypeId === BASELINE_TYPE.TARGET) {
      duration = mainDuration; // Only use main duration for target baseline
    }
    let end = moment(startDate).endOf('day').add(duration, 'days');
    return { start: start, end: end, duration: duration };
  }, [selectedBaseline, startDate, endDate]);

  const getBaselineObj = (viewByUnit) => {
    let base = {};
    let baseStart = moment.parseZone(selectedBaseline.startDate).startOf(viewByUnit);
    let baseEnd = moment.parseZone(selectedBaseline.endDate);
    while (baseStart.isSameOrBefore(baseEnd)) {
      let dayKey = baseStart.clone().format(UTC_TIMESTAMP);
      if (base[dayKey] === undefined) {
        base[dayKey] = 0;
      }
      baseStart.add(1, viewByUnit);
    }
    return base;
  };

  const getResult = (main, base, displayUnit, dataType) => {
    let resMain = [];
    let resBase = [];
    let baselineData = Object.keys(base)
      .sort()
      .map((key) => base[key]);
    let i = 0;
    let mainNext = 0;
    let baseNext = 0;
    let max = 0;

    for (const [key, value] of Object.entries(main)) {
      if (dataType === DATA_TYPE.ACCUMULATED) {
        mainNext = value != null ? (mainNext ?? 0) + value : null;
        baseNext += baselineData[i] ?? 0;
      } else {
        mainNext = value;
        baseNext = baselineData[i];
      }
      max = max < mainNext ? mainNext : max;
      max = max < baseNext ? baseNext : max;
      resMain.push({ x: key, y: mainNext });
      resBase.push({ x: key, y: baseNext });
      i++;
    }
    const { title, converted } = getUnitTitle(max, displayUnit);
    if (converted) {
      resBase = resBase.map(({ x, y }) => {
        if (y !== null) {
          return { x: x, y: parseFloat(convertUnitValue(y, displayUnit, false)) };
        }
        return { x: x, y: y };
      });
      resMain = resMain.map(({ x, y }) => {
        if (y !== null) {
          return { x: x, y: parseFloat(convertUnitValue(y, displayUnit, false)) };
        }
        return { x: x, y: y };
      });
    }
    return {
      mainSeries: resMain.sort((a, b) => moment.utc(a.x).valueOf() - moment.utc(b.x).valueOf()),
      baseSeries: resBase.sort((a, b) => moment.utc(a.x).valueOf() - moment.utc(b.x).valueOf()),
      unit: title,
      max: converted ? parseFloat(convertUnitValue(max, displayUnit)) : max
    };
  };

  const getPeriodicOverviewSeries = useCallback(
    (mainRigData, baselineData, selectedCategoryData) => {
      const { dataType, viewBy } = periodicOverview;
      let cat = [];
      let main = {};
      let base = {};
      const isTargetBaseline = baselineData.baselineTypeId === BASELINE_TYPE.TARGET;
      let viewByUnit = viewBy === VIEW_BY_TYPE.DAILY ? 'day' : 'hour';
      let startClone = moment(start.clone().startOf('day').format(UTC_TIMESTAMP)).startOf('day');

      while (startClone.isSameOrBefore(end)) {
        let dayKey = startClone.clone().format(UTC_TIMESTAMP);
        cat.push(dayKey);
        main[dayKey] = 0;
        if (isTargetBaseline) {
          // if (end.isAfter(moment.parseZone(baselineData.endDate))) {
          //   base[dayKey] = 0;
          // }
          base[dayKey] = getTargetBaselineValue(viewBy);
        }

        startClone.add(1, viewByUnit);
      }

      selectedCategoryData.forEach(({ key }) => {        
        let mainDataSignalTimeSeries = findDrillingEvent(mainRigData, key, displayUnit)?.timeSeriesValues || {};
        if (mainDataSignalTimeSeries) {
          Object.keys(mainDataSignalTimeSeries).forEach((key) => {
            let dayKey = moment.parseZone(key).startOf(viewByUnit).format(UTC_TIMESTAMP);
            main[dayKey] += getValue(mainDataSignalTimeSeries[key]);
          });
        }

        if (!isTargetBaseline) {
          let baselineDataSignalTimeSeries = findDrillingEvent(baselineData, key, displayUnit)?.timeSeriesValues || {};
          if (baselineDataSignalTimeSeries) {
            Object.keys(baselineDataSignalTimeSeries).forEach((key) => {
              let dayKey = moment.parseZone(key).startOf(viewByUnit).format(UTC_TIMESTAMP);
              if (base[dayKey] === undefined) {
                base[dayKey] = 0;
              }
              base[dayKey] += getValue(baselineDataSignalTimeSeries[key]);
            });
          }
        }
      });

      nullOutIfEmpty(main);

      return { ...getResult(main, base, displayUnit, dataType), categories: cat };
    },
    [displayUnit, selectedBaseline, startDate, endDate, periodicOverview]
  );

  const getEquipmentSeries = useCallback(
    (mainRigData, baselineData, allEquipments) => {
      const { dataType, viewBy } = equipment;
      let cat = [];
      let main = {};
      let base = {};
      let viewByUnit = viewBy === VIEW_BY_TYPE.DAILY ? 'day' : 'hour';

      let startClone = moment(start.clone().startOf('day').format(UTC_TIMESTAMP)).startOf('day');
      while (startClone.isSameOrBefore(end)) {
        let dayKey = startClone.clone().format(UTC_TIMESTAMP);
        cat.push(dayKey);
        main[dayKey] = 0;
        startClone.add(1, viewByUnit);
      }

      allEquipments.forEach(({ key }) => {
        let mainDataSignalTimeSeries = findEquipmentSignal(mainRigData, key, displayUnit)?.timeSeriesValues || {};
        if (mainDataSignalTimeSeries) {
          Object.keys(mainDataSignalTimeSeries).forEach((key) => {
            let dayKey = moment.parseZone(key).startOf(viewByUnit).format(UTC_TIMESTAMP);
            main[dayKey] += getValue(mainDataSignalTimeSeries[key]);
          });
        }

        let baselineDataSignalTimeSeries = findEquipmentSignal(baselineData, key, displayUnit)?.timeSeriesValues || {};
        if (baselineDataSignalTimeSeries) {
          Object.keys(baselineDataSignalTimeSeries).forEach((key) => {
            let dayKey = moment.parseZone(key).startOf(viewByUnit).format(UTC_TIMESTAMP);
            if (base[dayKey] === undefined) {
              base[dayKey] = 0;
            }
            base[dayKey] += getValue(baselineDataSignalTimeSeries[key]);
          });
        }
      });

      nullOutIfEmpty(main);

      return { ...getResult(main, base, displayUnit, dataType), categories: cat };
    },
    [displayUnit, selectedBaseline, start, end, equipment]
  );

  const getDrillingSeries = useCallback(
    (mainRigData, baselineData) => {
      const { dataType, activityType, viewBy } = drillingActivities;
      
      let cat = [];
      let main = {};
      let viewByUnit = viewBy === VIEW_BY_TYPE.DAILY ? 'day' : 'hour';
      let base = getBaselineObj(viewByUnit);

      let startClone = moment(start.clone().startOf('day').format(UTC_TIMESTAMP)).startOf('day');
      while (startClone.isSameOrBefore(end)) {
        let dayKey = startClone.clone().format(UTC_TIMESTAMP);
        cat.push(dayKey);
        main[dayKey] = 0;
        startClone.add(1, viewByUnit);
      }
      
      activityType?.forEach(({ name }) => {
        let mainData = mainRigData.find(
          ({ activity }) => name.toLowerCase() === activity.toLowerCase()
        );
        let baseData = baselineData.find(
          ({ activity }) => name.toLowerCase() === activity.toLowerCase()
        );

        if (mainData) {
          const mainEvent = findDrillingEvent(mainData.events, 'Drilling', displayUnit);
          Object.entries(mainEvent?.timeSeriesValues ?? {}).forEach(([key, value]) => {
            let dayKey = moment.parseZone(key).startOf(viewByUnit).format(UTC_TIMESTAMP);
            if (main[dayKey] === undefined) {
              main[dayKey] = 0;
            }
            main[dayKey] += getValue(value);
          });
        }
        if (baseData) {
          const baseEvent = findDrillingEvent(baseData.events, 'Drilling', displayUnit);
          Object.entries(baseEvent?.timeSeriesValues ?? {}).forEach(([key, value]) => {
            let dayKey = moment.parseZone(key).startOf(viewByUnit).format(UTC_TIMESTAMP);
            if (base[dayKey] === undefined) {
              base[dayKey] = 0;
            }
            base[dayKey] += getValue(value);
          });
        }
      });

      nullOutIfEmpty(main);

      return { ...getResult(main, base, displayUnit, dataType), categories: cat };
    },
    [displayUnit, selectedBaseline, start, end, drillingActivities]
  );

  const convertAnalyticsData = (data, valueKey, focusAreaKeys, viewBy, resRef) => {
    const res = { ...resRef };
    data.forEach((obj) => {
      if (!focusAreaKeys.includes(obj.type)) return;
      let eventStart = moment.parseZone(obj.startDate);
      let eventEnd = moment.parseZone(obj.endDate);
      let eventDuration = moment.duration(eventEnd.diff(eventStart)).asMinutes();
      let eventValue = obj[valueKey];
      let eventMinuteValue = eventValue / eventDuration;
      let viewByUnit = viewBy === VIEW_BY_TYPE.DAILY ? 'day' : 'hour';
      if (eventStart.isSame(eventEnd, viewByUnit)) {
        // If startDate and endDate is in the same unit. i.e same hour or day
        let timeKey = eventStart.startOf(viewByUnit).format(UTC_TIMESTAMP);
        if (res[timeKey] === undefined) {
          res[timeKey] = 0;
        }
        res[timeKey] += getValue(eventValue);
      } else {
        let startClone = eventStart.clone();
        while (startClone.isSameOrBefore(eventEnd)) {
          let nextTime = startClone.clone().startOf(viewByUnit).add(1, viewByUnit);
          if (nextTime.isAfter(eventEnd)) {
            nextTime = eventEnd.clone();
          }
          let timeKey = startClone.clone().startOf(viewByUnit).format(UTC_TIMESTAMP);
          let currDuration = moment.duration(nextTime.diff(startClone)).asMinutes();

          if (res[timeKey] === undefined) {
            res[timeKey] = 0;
          }
          res[timeKey] += eventMinuteValue * currDuration;
          startClone = nextTime.clone();
          if (nextTime.isSame(eventEnd)) {
            break;
          }
        }
      }
    });
    return res;
  };

  const getAnalyticsTargetValue = useCallback(
    (baselineData, focusArea, viewBy) => {
      let res = 0;
      focusArea.forEach(({ value: id }) => {
        switch (id) {
          case 1:
            res += baselineData?.[`engines${displayUnit}Waste`] ?? 0;
            break;
          case 2:
            res += baselineData?.[`mudpumps${displayUnit}Waste`] ?? 0;
            break;
          case 3:
            res += baselineData?.[`hpu${displayUnit}Waste`] ?? 0;
            break;
          case 4:
            res += baselineData?.[`dw${displayUnit}Waste`] ?? 0;
            break;
          case 5:
            res += baselineData?.[`agitators${displayUnit}Waste`] ?? 0;
            break;
        }
      });

      if (baselineData.daily) {
        if (viewBy === VIEW_BY_TYPE.HOURLY) {
          return res / 24;
        }
        return res;
      }

      if (viewBy === VIEW_BY_TYPE.DAILY) {
        return res / mainDurationDays;
      }
      return res / mainDurationHours;
    },
    [displayUnit, mainDurationDays, mainDurationHours]
  );

  const getAnalyticsSeries = useCallback(
    (mainRigData, baselineData) => {
      const { dataType, focusArea, viewBy } = analytics;
      let cat = [];
      let main = {};
      let viewByUnit = viewBy === VIEW_BY_TYPE.DAILY ? 'day' : 'hour';
      const isTargetBaseline = baselineData.baselineTypeId === BASELINE_TYPE.TARGET;
      let base = {};

      let targetValue = null;
      if (isTargetBaseline) {
        targetValue = getAnalyticsTargetValue(baselineData, focusArea, viewBy);
      } else {
        base = getBaselineObj(viewByUnit);
      }

      let startClone = moment(start.clone().startOf('day').format(UTC_TIMESTAMP)).startOf('day');
      while (startClone.isSameOrBefore(end)) {
        let dayKey = startClone.clone().format(UTC_TIMESTAMP);
        cat.push(dayKey);
        main[dayKey] = 0;
        startClone.add(1, viewByUnit);
        if (isTargetBaseline) {
          base[dayKey] = targetValue;
        }
      }

      let valueKey = `${displayUnit.toLowerCase()}Saving`;
      let focusAreaKeys = focusArea?.map(({ key }) => key);
      main = convertAnalyticsData(mainRigData, valueKey, focusAreaKeys, viewBy, main);
      if (!isTargetBaseline) {
        base = convertAnalyticsData(baselineData, valueKey, focusAreaKeys, viewBy, base);
      } else {
        Object.keys({ ...main }).forEach((key) => {
          main[key] = roundToSixDecimals(main[key]);
        });
      }

      nullOutIfEmpty(main);

      return { ...getResult(main, base, displayUnit, dataType), categories: cat };
    },
    [displayUnit, selectedBaseline, start, end, analytics]
  );

  const getPowerMgmtSeries = useCallback(
    (mainRigData, baselineData) => {
      const { viewBy, dataUnit } = powerMgmt;
      let cat = [];
      let main = {};
      let mainEngOn = {};
      let base = {};
      let baseEngOn = {};
      let viewByUnit = viewBy === VIEW_BY_TYPE.DAILY ? 'day' : 'hour';
      
      const mainSignals = mainRigData?.signals || [];
      const baseSignals = baselineData?.signals || [];
      const mainGeneratorMap = getGeneratorSignalsMap(mainSignals);
      const baseGeneratorMap = getGeneratorSignalsMap(baseSignals);
      const mainNumEng = Object.keys(mainGeneratorMap).length;
      const secNumEng = Object.keys(baseGeneratorMap).length;

      let startClone = moment(start.clone().startOf('day').format(UTC_TIMESTAMP)).startOf('day');
      while (startClone.isSameOrBefore(end)) {
        let dayKey = startClone.clone().format(UTC_TIMESTAMP);
        main[dayKey] = null;
        base[dayKey] = null;
        mainEngOn[dayKey] = 0;
        baseEngOn[dayKey] = { count: 0, value: 0 };
        cat.push(dayKey);
        startClone.add(1, viewByUnit);
      }

      // Process EngOn data for main rig
      const mainEngOnSignal = findSignal(mainSignals, 'EngOn');
      const mainEngOnData = mainEngOnSignal?.timeSeriesValues || {};
      Object.entries(mainEngOnData).forEach(([key, value]) => {
        let dayKey = moment.parseZone(key).startOf(viewByUnit).format(UTC_TIMESTAMP);
        mainEngOn[dayKey] += getValue(value);
      });
      mainEngOn = Object.keys(mainEngOn).map((key) => {
        let val = mainEngOn[key];
        if (viewBy === VIEW_BY_TYPE.DAILY) {
          val = val / 24;
        }
        return { x: key, y: val };
      });

      if (!mainEngOn.some(item => item.y !== 0)) {
        mainEngOn = mainEngOn.map(item => ({ ...item, y: null }));
      }
      
      // Process EngOn data for baseline
      const baseEngOnSignal = findSignal(baseSignals, 'EngOn');
      const baseEngOnData = baseEngOnSignal?.timeSeriesValues || {};
      Object.values(baseEngOnData).forEach((value, i) => {
        let index = i;
        if (viewBy === VIEW_BY_TYPE.DAILY) {
          index = Math.floor(i / 24);
        }
        let dayKey = moment.utc(cat[index]).startOf(viewByUnit).format(UTC_TIMESTAMP);
        let count = isNaN(value) ? 0 : 1;
        baseEngOn[dayKey] = {
          count: baseEngOn[dayKey]?.count + count,
          value: baseEngOn[dayKey]?.value + getValue(value)
        };
      });
      baseEngOn = Object.keys(baseEngOn).map((key) => {
        let val = baseEngOn[key].value;
        if (viewBy === VIEW_BY_TYPE.DAILY) {
          return { x: key, y: val / (baseEngOn[key].count || 1) };
        }
        return { x: key, y: val };
      });

      let mainEngDayCount = {};
      const metricKey = dataUnit === SFOC_OR_LOAD.SFOC ? 'SFOC' : 'Load';
      
      // Iterate through each main generator
      Object.entries(mainGeneratorMap).forEach(([generatorNum, metrics]) => {
        let tempEngDayCount = {};
        let tempMain = {};
        
        const engineData = metrics[metricKey] || {};
        
        Object.entries(engineData).forEach(([key, value]) => {
          let dayKey = moment.parseZone(key).startOf(viewByUnit).format(UTC_TIMESTAMP);
          if (value !== 'null' && value !== null) {
            tempEngDayCount[dayKey] = true;
            tempMain[dayKey] = {
              count: (tempMain[dayKey]?.count ?? 0) + 1,
              value: (tempMain[dayKey]?.value ?? 0) + getValue(value)
            };
          }
        });
        Object.keys(tempEngDayCount).forEach((key) => {
          mainEngDayCount[key] = (mainEngDayCount[key] ?? 0) + 1;
        });

        Object.keys(tempMain).forEach((key) => {
          let val = tempMain[key].value;
          let count = tempMain[key]?.count || 1;

          if (viewBy === VIEW_BY_TYPE.DAILY) {
            main[key] += val / count;
          } else {
            main[key] += val;
          }
        });
      });
      
      let baseEngDayCount = {};
      // Iterate through each baseline generator
      Object.entries(baseGeneratorMap).forEach(([generatorNum, metrics]) => {
        let tempEngDayCount = {};
        let tempBase = {};
        
        const engineData = metrics[metricKey] || {};
        
        Object.values(engineData).forEach((value, i) => {
          let index = i;
          if (viewBy === VIEW_BY_TYPE.DAILY) {
            index = Math.floor(i / 24);
          }

          let dayKey = moment.utc(cat[index]).startOf(viewByUnit).format(UTC_TIMESTAMP);
          if (value !== 'null' && value !== null) {
            tempEngDayCount[dayKey] = true;
            tempBase[dayKey] = {
              count: (tempBase[dayKey]?.count ?? 0) + 1,
              value: (tempBase[dayKey]?.value ?? 0) + getValue(value)
            };
          }
        });
        Object.keys(tempEngDayCount).forEach((key) => {
          baseEngDayCount[key] = (baseEngDayCount[key] ?? 0) + 1;
        });
        Object.keys(tempBase).forEach((key) => {
          let val = tempBase[key].value;
          if (viewBy === VIEW_BY_TYPE.DAILY) {
            val = val / (tempBase[key]?.count || 1);
          }
          base[key] += val;
        });
      });
      
      Object.keys(main).forEach((key) => {
        if (main[key] !== null) {
          main[key] = main[key] / mainEngDayCount[key];
        }
      });
      Object.keys({ ...base }).forEach((key) => {
        if (base[key] !== null) {
          base[key] = base[key] / baseEngDayCount[key];
        }
      });
      mainEngOn = mainEngOn.sort((a, b) => moment.utc(a.x).valueOf() - moment.utc(b.x).valueOf());
      baseEngOn = baseEngOn.sort((a, b) => moment.utc(a.x).valueOf() - moment.utc(b.x).valueOf());
      let secMax = secNumEng > mainNumEng ? secNumEng : mainNumEng;
      return {
        ...getResult(main, base, DISPLAY_UNIT.ENERGY, DATA_TYPE.DISCRETE),
        mainEngOnSeries: mainEngOn,
        baseEngOnSeries: baseEngOn,
        secMax: secMax
      };
    },
    [selectedBaseline, start, end, powerMgmt]
  );

  return {
    getPeriodicOverviewSeries,
    getDrillingSeries,
    getEquipmentSeries,
    getAnalyticsSeries,
    getPowerMgmtSeries
  };
};
